import React, { useContext, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedResource } from "../../../store/compliance/complianceSlice";
import RelatedControlsModal from "./RelatedControlsModal";
import { formatDateTime } from "../AuditHub/utils";
import { CloudProviderContext } from "../../../CloudProviderContext";

const StyledChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor:
    status === "FAILED"
      ? "#ffebee"
      : status === "MANUAL"
      ? "#e3f2fd"
      : "#e8f5e9",
  color:
    status === "FAILED"
      ? "#d32f2f"
      : status === "MANUAL"
      ? "#1976d2"
      : "#2e7d32",
  fontWeight: 600,
  fontSize: "0.75rem",
  height: 24,
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

// FilterChip copied and adapted from ControlsGrid.js
const FilterChip = ({
  label,
  count,
  type,
  active,
  onClick,
  disabled = false,
}) => {
  // Color logic
  let bg = "#f5f5f5",
    color = "#333",
    border = "none";
  if (type === "passed") {
    bg = active ? "#c8e6c9" : "#e8f5e9";
    color = "#2e7d32";
    border = active ? "2px solid #2e7d32" : "none";
  } else if (type === "failed") {
    bg = active ? "#ffcdd2" : "#ffebee";
    color = "#d32f2f";
    border = active ? "2px solid #d32f2f" : "none";
  } else if (type === "manual") {
    bg = active ? "#bbdefb" : "#e3f2fd";
    color = "#1976d2";
    border = active ? "2px solid #1976d2" : "none";
  }
  return (
    <Chip
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption">{label}:</Typography>
          <Typography variant="caption" fontWeight={600}>
            {count}
          </Typography>
          {!disabled &&
            (active ? (
              <RemoveIcon sx={{ fontSize: 16, ml: 0.5 }} />
            ) : (
              <AddIcon sx={{ fontSize: 16, ml: 0.5 }} />
            ))}
        </Box>
      }
      onClick={disabled ? undefined : onClick}
      size="small"
      sx={{
        height: "24px",
        backgroundColor: bg,
        color,
        border,
        fontWeight: 600,
        fontSize: "0.75rem",
        cursor: disabled ? "default" : "pointer",
        mr: 1,
        "& .MuiChip-label": { color },
        opacity: disabled ? 0.7 : 1,
        transition: "all 0.2s ease-in-out",
      }}
      clickable={!disabled}
      variant={active ? "filled" : "outlined"}
      disabled={disabled}
    />
  );
};

const ResourcesGrid = ({
  header = {},
  resources = [],
  onChecksClick,
  availableTypes,
  availableRegions,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedProvider } = useContext(CloudProviderContext);
  // Filters
  const [selectedType, setSelectedType] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedControls, setSelectedControls] = useState(null);
  const [isControlsModalOpen, setIsControlsModalOpen] = useState(false);
  const [statusFilters, setStatusFilters] = useState({
    PASSED: false,
    FAILED: false,
  });
  const [search, setSearch] = useState("");

  // Use header values for dropdowns
  const types =
    availableTypes && availableTypes.length > 0
      ? availableTypes
      : header.AvailableTypes || [];
  const regions =
    availableRegions && availableRegions.length > 0
      ? availableRegions
      : header.AvailableRegions || [];

  // Metrics from resources data
  const total = resources.length;
  const passed = resources.filter((r) => r.OverallStatus === "PASSED").length;
  const failed = resources.filter((r) => r.OverallStatus === "FAILED").length;

  const handleStatusFilterClick = (status) => {
    setStatusFilters((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  // Filtering
  const filteredRows = resources.filter((row) => {
    let match = true;
    if (selectedType) match = match && row.Service === selectedType;
    if (selectedRegion)
      match = match && row.Checks?.some((c) => c.Region === selectedRegion);

    // Status filtering
    const activeStatusFilters = Object.entries(statusFilters)
      .filter(([_, isActive]) => isActive)
      .map(([status]) => status);
    if (activeStatusFilters.length > 0) {
      match = match && activeStatusFilters.includes(row.OverallStatus);
    }

    // Search filtering (case-insensitive, checks all string fields)
    if (search && search.trim() !== "") {
      const searchLower = search.toLowerCase();
      const valuesToSearch = [
        row.ResourceArn,
        row.Service,
        row.OverallStatus,
        ...(row.Checks || []).map((c) => c.Region),
        ...(row.Checks || []).map((c) => c.Status),
      ];
      match =
        match &&
        valuesToSearch.some(
          (val) => val && String(val).toLowerCase().includes(searchLower)
        );
    }

    return match;
  });
  console.log("🚀 ~ ResourcesGrid ~ filteredRows:", filteredRows);

  // Columns
  const columns = [
    {
      field: "ResourceArn",
      headerName:
        selectedProvider === "AZURE" ? "Azure Resource ID" : "Resource ARN",
      flex: 1,
      minWidth: 300,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: "inherit",
            fontSize: "0.875rem",
            width: "100%",
            textAlign: "left",
            m: 0,
            p: 0,
            lineHeight: 1.2,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "OverallStatus",
      headerName: "Overall Status",
      width: 130,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            color:
              params.value === "FAILED"
                ? "#d32f2f"
                : params.value === "MANUAL"
                ? "#1976d2"
                : params.value === "PASSED"
                ? "#2e7d32"
                : "#888",
            fontSize: 15,
            m: 0,
            p: 0,
            lineHeight: 1.2,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "Checks",
      headerName: "Checks",
      width: 110,
      headerAlign: "center",
      align: "center",
      sortable: true,
      sortComparator: (v1, v2) => (v1?.length || 0) - (v2?.length || 0),
      renderCell: (params) => {
        const count = params.value?.length || 0;
        return (
          <Tooltip title={`${count} checks`}>
            <span>
              <Chip
                label={count}
                size="small"
                onClick={
                  count === 0 ? undefined : () => handleChecksClick(params.row)
                }
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 24,
                  "& .MuiChip-label": { color: "#1976d2" },
                  cursor: count === 0 ? "not-allowed" : "pointer",
                  opacity: count === 0 ? 0.5 : 1,
                  filter: count === 0 ? "blur(0.5px) grayscale(0.5)" : "none",
                }}
                clickable={count !== 0}
                disabled={count === 0}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "RelatedControlStatuses",
      headerName: "Related Control Statuses",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: true,
      sortComparator: (v1, v2) => (v1?.length || 0) - (v2?.length || 0),
      renderCell: (params) => {
        const count = params.value?.length || 0;
        return (
          <Tooltip title={`${count} related statuses`}>
            <span>
              <Chip
                label={count}
                size="small"
                onClick={
                  count === 0
                    ? undefined
                    : () =>
                        handleControlsClick(
                          params.value,
                          params.row.ResourceArn
                        )
                }
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 24,
                  "& .MuiChip-label": { color: "#1976d2" },
                  cursor: count === 0 ? "not-allowed" : "pointer",
                  opacity: count === 0 ? 0.5 : 1,
                  filter: count === 0 ? "blur(0.5px) grayscale(0.5)" : "none",
                  "&:hover": {
                    backgroundColor: count === 0 ? "#e3f2fd" : "#bbdefb",
                  },
                }}
                clickable={count !== 0}
                disabled={count === 0}
              />
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const handleChecksClick = (resource) => {
    if (typeof onChecksClick === "function") {
      onChecksClick(resource);
    } else {
      dispatch(setSelectedResource(resource));
      const params = new URLSearchParams({
        controlId: header.ControlId,
        resourceArn: resource.ResourceArn,
      });
      history.push(`/compliance/controls/checks?${params.toString()}`);
    }
  };

  const handleControlsClick = (controls, resourceArn) => {
    setSelectedControls({ controls, resourceArn });
    setIsControlsModalOpen(true);
  };

  return (
    <Box
      sx={{ flexGrow: 1, overflow: "hidden", height: "calc(100vh - 200px)" }}
    >
      {/* Main header with chips, filters, and search bar on the right */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ flexWrap: "wrap", gap: 2 }}
        >
          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Framework:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color:"text.secondary", fontWeight: 500, fontSize: "0.95rem" }}
            >
              {header.Framework || "-"}
            </Typography>
          </Box> */}
          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Account Number:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color:"text.secondary", fontWeight: 500, fontSize: "0.95rem" }}
            >
              {header.AccountNumber || "-"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Scan Date:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color:"text.secondary", fontWeight: 500, fontSize: "0.95rem" }}
            >
              {header.ScanDate ? formatDateTime(header.ScanDate) || "-" : "-"}
            </Typography>
          </Box> */}
            {/* <Typography color="text.secondary" sx={{ mx: 1 }}>
              |
            </Typography> */}
          <FilterChip label="Total" count={total} type="total" disabled />
          <FilterChip
            label="Passed"
            count={passed}
            type="passed"
            active={statusFilters.PASSED}
            onClick={() => handleStatusFilterClick("PASSED")}
          />
          <FilterChip
            label="Failed"
            count={failed}
            type="failed"
            active={statusFilters.FAILED}
            onClick={() => handleStatusFilterClick("FAILED")}
          />
          {/* Add Manual if needed */}
          <Typography color="text.secondary" sx={{ mx: 1 }}>
            |
          </Typography>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              background: "#fff",
              borderRadius: 2,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}
          >
            <InputLabel>ServiceType</InputLabel>
            <Select
              value={selectedType}
              label="Service Type"
              onChange={(e) => setSelectedType(e.target.value)}
              sx={{
                height: 32,
                fontWeight: 600,
                color: "#1976d2",
                fontSize: "0.875rem",
                background: "#fff",
                borderRadius: 2,
                boxShadow: "none",
                minHeight: 40,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2,
                    boxShadow: "0 2px 12px rgba(25, 118, 210, 0.08)",
                    mt: 1,
                  },
                },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {types.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: 120,
              background: "#fff",
              borderRadius: 2,
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}
          >
            <InputLabel>Region</InputLabel>
            <Select
              value={selectedRegion}
              label="Region"
              onChange={(e) => setSelectedRegion(e.target.value)}
              sx={{
                height: 32,
                fontWeight: 600,
                color: "#1976d2",
                fontSize: "0.875rem",
                background: "#fff",
                borderRadius: 2,
                boxShadow: "none",
                minHeight: 40,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2,
                    boxShadow: "0 2px 12px rgba(25, 118, 210, 0.08)",
                    mt: 1,
                  },
                },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {regions.map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Search bar on the right */}
          <Box sx={{ flexGrow: 1 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#F1F1F1",
              borderRadius: 1,
              px: 1,
              height: 36,
            }}
          >
            <SearchIcon sx={{ color: "#888", fontSize: 20, mr: 1 }} />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: 200,
                fontSize: 14,
                border: "none",
                outline: "none",
                background: "transparent",
                color: "#222",
              }}
            />
          </Box>
        </Stack>
      </Paper>
      <DataGrid
        rows={filteredRows.map((row, idx) => ({
          id: row.SerialNumber || row.ResourceArn || idx,
          ...row,
        }))}
        columns={columns}
        pageSize={50}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
        rowHeight={40}
        getRowId={(row) => row.SerialNumber || row.ResourceArn}
        sx={{
          height: "70vh",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            fontSize: "14px",
            fontFamily: "inherit",
            py: 0,
            px: 1,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F5F5F5",
            fontWeight: "bold",
            fontSize: "14px",
            color: "#333",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#F5F5F5",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: "white" },
          "& .MuiDataGrid-row": {
            "&:hover": { backgroundColor: "#f5f5f5" },
            "&.Mui-selected": {
              backgroundColor: "#e3f2fd",
              "&:hover": { backgroundColor: "#e3f2fd" },
            },
          },
          "& .MuiDataGrid-cell:focus": { outline: "none" },
          "& .MuiDataGrid-columnHeader:focus": { outline: "none" },
        }}
      />
      {selectedControls && (
        <RelatedControlsModal
          open={isControlsModalOpen}
          onClose={() => {
            setIsControlsModalOpen(false);
            setSelectedControls(null);
          }}
          controls={selectedControls.controls}
          resourceArn={selectedControls.resourceArn}
        />
      )}
    </Box>
  );
};

export default ResourcesGrid;
